import React, { Fragment, useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showHttpError, showToast, showConfirm, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─── API config ──────────────────────────────────────────────────
const BASE_URL = `${WEBSERVICE}/shopping`;
const getMerchantId = () =>
  parseInt(localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "0", 10);
const getToken = () =>
  getAuthToken() || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid(),
});

const api = {
  fetchAll: (merchantId) =>
    fetch(`${BASE_URL}/discounts?merchantId=${merchantId}`, {
      headers: authHeaders(),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  batch: (payload) =>
    fetch(`${BASE_URL}/discounts/batch`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  remove: (merchantId, id) =>
    fetch(`${BASE_URL}/discounts/${merchantId}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),
};

// ─── Helpers ─────────────────────────────────────────────────────
/**
 * Group flat API records by profileName → profiles array used by the UI.
 * Each rule keeps its API id so batch/DELETE can reference it later.
 */
function groupIntoProfiles(records) {
  const map = {};
  records.forEach((rec) => {
    const key = rec.profileName;
    if (!map[key]) {
      map[key] = {
        id: key,
        name: rec.profileName,
        isDefaultProfile: rec.isDefaultProfile,
        rules: [],
      };
    } else if (rec.isDefaultProfile === 1 || rec.isDefaultProfile === true) {
      // If any record for this profile is marked as default, set it as default
      map[key].isDefaultProfile = rec.isDefaultProfile;
    }
    map[key].rules.push({
      id: rec.id,
      cartThreshold: String(rec.minimumCartValue),
      discountType: "percent",
      discountValue: String(rec.discountPercentage),
    });
  });
  return Object.values(map);
}

// ─── Styles ──────────────────────────────────────────────────────
// ─── Responsive Styles ──────────────────────────────────────────
const ResponsiveStyles = () => (
  <style>{`
    .dp-wrapper { padding: 36px 24px 60px; }
    .dp-header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; flex-wrap: wrap; gap: 16px; }
    .dp-card-header { display: flex; align-items: center; gap: 12px; padding: 20px 28px; border-bottom: 1px solid var(--body-color-less2); }
    .dp-card-body { padding: 24px 28px; }
    .dp-rule-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; }
    .dp-rule-inputs { display: flex; align-items: center; gap: 10px; flex: 1; }
    .dp-rule-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 22px; padding-top: 18px; border-top: 1px solid var(--body-color-less2); }
    .dp-rule-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-radius: 10px; background: var(--body-color); border: 1px solid var(--body-color-less2); }

    @media (max-width: 1024px) {
      .dp-rule-row { flex-wrap: wrap; }
      .dp-rule-summary { width: 100%; margin-right: 0 !important; margin-top: 8px; }
    }

    @media (max-width: 768px) {
      .dp-wrapper { padding: 20px 16px 40px; }
      .dp-header-row { flex-direction: column; gap: 12px; }
      .dp-card-header { flex-direction: column; align-items: flex-start; padding: 16px; }
      .dp-card-header-actions { margin-left: 0 !important; width: 100%; margin-top: 12px; justify-content: space-between; }
      .dp-card-body { padding: 16px; }
      .dp-rule-row { flex-direction: column; align-items: stretch; padding: 16px; }
      .dp-rule-inputs { flex-direction: row; flex-wrap: wrap; align-items: center; justify-content: center; }
      .dp-rule-index { margin-right: 0 !important; margin-bottom: 8px; }
      .dp-rule-arrow { transform: rotate(90deg); margin: 5px 0 !important; }
      .dp-rule-actions { flex-direction: column-reverse; align-items: stretch; }
      .dp-rule-actions > button { width: 100% !important; justify-content: center !important; }
      .dp-rule-item { flex-wrap: wrap; }
      .dp-rule-item > span:nth-child(2) { width: 100%; margin-bottom: 4px; }
    }

    @media (max-width: 480px) {
        .dp-rule-inputs { flex-direction: column; align-items: stretch; }
        .dp-rule-input-group { width: 100% !important; margin-right: 0 !important; }
        .dp-rule-label { width: 100%; text-align: center; margin-right: 0 !important; margin-bottom: 4px; }
    }
  `}</style>
);

const S = {
  pageWrapper: { }, // Moved to .dp-wrapper
  headerRow: { }, // Moved to .dp-header-row
  pageTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
  card: {
    background: "var(--body-color-less)",
    borderRadius: "16px",
    border: "1px solid var(--body-color-less2)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: "24px",
    overflow: "hidden"
  },
  cardHeader: { }, // Moved to .dp-card-header
  cardHeaderIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    flexShrink: 0,
  },
  cardHeaderTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  cardBody: { }, // Moved to .dp-card-body
  formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: "var(--text-color)",
    opacity: 0.7,
  },
  input: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  inputError: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid #ef4444",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  btnPrimary: {
    padding: "10px 24px",
    background: "var(--btn-color)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnOutline: {
    padding: "8px 18px",
    background: "transparent",
    border: "1px solid var(--btn-color)",
    color: "var(--btn-color)",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnDanger: {
    padding: "6px 14px",
    background: "rgba(239,68,68,0.1)",
    border: "none",
    color: "#ef4444",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  errorText: { fontSize: "11px", color: "#ef4444", marginTop: "2px" },
};

const MAX_RULES = 50;

const RULE_COLORS = [
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#f97316",
  "#84cc16",
  "#a78bfa",
];
const ruleColor = (idx) => RULE_COLORS[idx % RULE_COLORS.length];

// ─── Rule row ────────────────────────────────────────────────────
function RuleRow({ rule, idx, onChange, onRemove, error }) {
  const color = ruleColor(idx);
  return (
    <div
      style={{
        background: "var(--body-color)",
        borderRadius: "12px",
        border: `1px solid color-mix(in srgb, ${color} 22%, var(--body-color-less2))`,
        overflow: "hidden",
        transition: "border-color 0.15s",
      }}
    >
      <div style={{ height: "3px", background: color }} />

      <div className="dp-rule-row">
        <div className="dp-rule-inputs">
          {/* Index */}
          <div
            className="dp-rule-index"
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: `color-mix(in srgb, ${color} 14%, transparent)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: "800",
              color,
              flexShrink: 0,
              marginRight: "14px",
            }}
          >
            {idx + 1}
          </div>

          {/* "If cart >" label */}
          <span
            className="dp-rule-label"
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "var(--text-color)",
              opacity: 0.55,
              flexShrink: 0,
              marginRight: "10px",
              whiteSpace: "nowrap",
            }}
          >
            If cart &gt;
          </span>

          {/* Cart threshold */}
          <div
            className="dp-rule-input-group"
            style={{
              position: "relative",
              width: "130px",
              flexShrink: 0,
              marginRight: "10px",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "14px",
                fontWeight: "700",
                color: "var(--text-color)",
                opacity: 0.45,
                pointerEvents: "none",
              }}
            >
              $
            </span>
            <input
              type="number"
              min="0"
              className="dp-input"
              style={{
                ...(error?.cartThreshold ? S.inputError : S.input),
                paddingLeft: "28px",
                paddingRight: "8px",
              }}
              value={rule.cartThreshold}
              onChange={(e) =>
                onChange({ ...rule, cartThreshold: e.target.value })
              }
              placeholder="0.00"
            />
          </div>

          {/* Arrow */}
          <span
            className="dp-rule-arrow"
            style={{
              fontSize: "18px",
              color,
              flexShrink: 0,
              margin: "0 10px",
              lineHeight: 1,
            }}
          >
            →
          </span>

          {/* Discount value — percentage only */}
          <div
            className="dp-rule-input-group"
            style={{
              position: "relative",
              width: "120px",
              flexShrink: 0,
              marginRight: "10px",
            }}
          >
            <input
              type="number"
              min="0"
              max="99"
              className="dp-input"
              style={{
                ...(error?.discountValue ? S.inputError : S.input),
                paddingRight: "32px",
              }}
              value={rule.discountValue}
              onChange={(e) =>
                onChange({ ...rule, discountValue: e.target.value })
              }
              placeholder="10"
            />
            <span
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "13px",
                fontWeight: "700",
                color: "var(--text-color)",
                opacity: 0.4,
                pointerEvents: "none",
              }}
            >
              %
            </span>
          </div>
        </div>

        {/* Summary chip */}
        {rule.cartThreshold && rule.discountValue && (
          <div
            className="dp-rule-summary"
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "5px 12px",
              borderRadius: "8px",
              background: `color-mix(in srgb, ${color} 8%, transparent)`,
              border: `1px solid color-mix(in srgb, ${color} 18%, transparent)`,
              marginRight: "10px",
              minWidth: 0,
            }}
          >
            <i
              className="mdi mdi-tag"
              style={{ fontSize: "12px", color, flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "var(--text-color)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Cart &gt; ${rule.cartThreshold} → {rule.discountValue}% off
            </span>
          </div>
        )}

        {/* Remove */}
        <button
          type="button"
          onClick={onRemove}
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: "rgba(239,68,68,0.1)",
            border: "none",
            color: "#ef4444",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="mdi mdi-close" style={{ fontSize: "15px" }} />
        </button>
      </div>

      {(error?.cartThreshold || error?.discountValue) && (
        <div style={{ padding: "0 16px 10px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {error.cartThreshold && (
            <span style={S.errorText}>
              <i
                className="mdi mdi-alert-circle"
                style={{ marginRight: "3px" }}
              />
              {error.cartThreshold}
            </span>
          )}
          {error.discountValue && (
            <span style={S.errorText}>
              <i
                className="mdi mdi-alert-circle"
                style={{ marginRight: "3px" }}
              />
              {error.discountValue}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Profile form ────────────────────────────────────────────────
function ProfileForm({ initial, onSave, onCancel, saving }) {
  const [name, setName] = useState(initial?.name || "");
  const [rules, setRules] = useState(
    initial?.rules?.length > 0
      ? initial.rules
      : [
          {
            id: null,
            cartThreshold: "",
            discountType: "percent",
            discountValue: "",
          },
        ]
  );
  const [deletedIds, setDeletedIds] = useState([]);
  const [isDefaultProfile, setisDefaultProfile] = useState(initial?.isDefaultProfile === 1 ? true : false);
  const [errors, setErrors] = useState({});

  const addRule = () => {
    if (rules.length >= MAX_RULES) return;
    setRules((prev) => [
      ...prev,
      {
        id: null,
        cartThreshold: "",
        discountType: "percent",
        discountValue: "",
      },
    ]);
  };

  const updateRule = (idx, updated) =>
    setRules((prev) => prev.map((r, i) => (i === idx ? updated : r)));

  const removeRule = (idx) => {
    const rule = rules[idx];
    if (rule.id !== null) {
      setDeletedIds((prev) => [...prev, rule.id]);
    }
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const e = {};
    const trimmedName = name.trim();
    if (!trimmedName) {
      e.name = "Profile name is required";
    } else if (trimmedName.length < 2 || trimmedName.length > 100) {
      e.name = "Profile name must be 2–100 characters";
    } else if (!/^[a-zA-Z0-9 \-&]+$/.test(trimmedName)) {
      e.name = "Only letters, numbers, spaces, hyphens, and & are allowed";
    }
    if (rules.length === 0) e.rules = "Add at least one rule";

    const ruleErrors = {};
    rules.forEach((r, idx) => {
      const re = {};
      if (
        !r.cartThreshold ||
        isNaN(parseFloat(r.cartThreshold)) ||
        parseFloat(r.cartThreshold) <= 0
      )
        re.cartThreshold = "Enter a valid cart threshold (> 0)";
      const dv = parseFloat(r.discountValue);
      if (!r.discountValue || isNaN(dv) || dv <= 0) {
        re.discountValue = "Enter a valid discount value (> 0)";
      } else if (dv > 99) {
        re.discountValue = "Percentage cannot exceed 99%";
      } else if (!/^\d+(\.\d{1,2})?$/.test(String(r.discountValue).trim())) {
        re.discountValue = "Maximum 2 decimal places allowed";
      }
      if (Object.keys(re).length) ruleErrors[idx] = re;
    });

    if (Object.keys(ruleErrors).length) e.ruleErrors = ruleErrors;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const sortedIndexMap = [...rules]
    .map((r, idx) => ({ idx, val: parseFloat(r.cartThreshold || 0) }))
    .sort((a, b) => a.val - b.val)
    .map((x) => x.idx);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      {/* Profile name */}
      <div style={S.formGroup}>
        <label style={S.label}>Profile Name *</label>
        <input
          className="dp-input"
          style={errors.name ? S.inputError : S.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Summer Sale 2025"
        />
        {errors.name && <span style={S.errorText}>{errors.name}</span>}
      </div>

      {/* Rules */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontSize: "13px",
                fontWeight: "700",
                color: "var(--text-color)",
              }}
            >
              Discount Rules
            </span>
            <span
              style={{
                padding: "2px 10px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: "700",
                background:
                  rules.length >= MAX_RULES
                    ? "rgba(239,68,68,0.1)"
                    : "color-mix(in srgb, var(--btn-color) 12%, transparent)",
                color:
                  rules.length >= MAX_RULES ? "#ef4444" : "var(--btn-color)",
              }}
            >
              {rules.length} / {MAX_RULES}
            </span>
          </div>
          <button
            type="button"
            onClick={addRule}
            disabled={rules.length >= MAX_RULES}
            style={{
              padding: "8px 18px",
              background: "var(--btn-color)",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: rules.length >= MAX_RULES ? "not-allowed" : "pointer",
              opacity: rules.length >= MAX_RULES ? 0.4 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="mdi mdi-plus" /> Add Rule
          </button>
        </div>

        {errors.rules && (
          <div style={{ ...S.errorText, marginBottom: "10px" }}>
            {errors.rules}
          </div>
        )}

        {rules.length === 0 && (
          <div
            style={{
              background: "var(--body-color)",
              borderRadius: "12px",
              border: "1px dashed var(--body-color-less2)",
              padding: "32px 24px",
              textAlign: "center",
            }}
          >
            <i
              className="mdi mdi-tag-multiple"
              style={{
                fontSize: "28px",
                color: "var(--text-color)",
                opacity: 0.2,
                display: "block",
                marginBottom: "10px",
              }}
            />
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-color)",
                opacity: 0.4,
              }}
            >
              No rules yet — click <strong>+ Add Rule</strong> to create your
              first discount rule
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {rules.map((rule, idx) => (
            <RuleRow
              key={`${rule.id ?? "new"}-${idx}`}
              rule={rule}
              idx={idx}
              onChange={(updated) => updateRule(idx, updated)}
              onRemove={() => removeRule(idx)}
              error={errors.ruleErrors?.[idx]}
            />
          ))}
        </div>

        {/* Preview ladder */}
        {rules.filter((r) => r.cartThreshold && r.discountValue).length > 1 && (
          <div
            style={{
              marginTop: "16px",
              background: "var(--body-color)",
              borderRadius: "12px",
              border: "1px solid var(--body-color-less2)",
              padding: "16px 20px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "var(--text-color)",
                opacity: 0.45,
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <i
                className="mdi mdi-sort-ascending"
                style={{ fontSize: "13px" }}
              />
              Rules Preview — sorted by cart value
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              {sortedIndexMap
                .map((origIdx) => rules[origIdx])
                .filter((r) => r.cartThreshold && r.discountValue)
                .map((r, si) => {
                  const color = ruleColor(si);
                  return (
                    <div
                      key={`prev-${si}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--text-color)",
                          opacity: 0.65,
                        }}
                      >
                        If cart &gt;
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "var(--text-color)",
                          fontFamily: "'SF Mono','Fira Code',monospace",
                        }}
                      >
                        ${parseFloat(r.cartThreshold).toFixed(2)}
                      </span>
                      <span
                        style={{ fontSize: "15px", color, fontWeight: "700" }}
                      >
                        →
                      </span>
                      <span
                        style={{ fontSize: "13px", fontWeight: "700", color }}
                      >
                        {r.discountValue}% off
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Default Profile Toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "16px 20px",
          borderRadius: "12px",
          border: "1px solid var(--body-color-less2)",
          background: "var(--body-color)",
        }}
      >
        <div
          onClick={() => setisDefaultProfile((prev) => !prev)}
          style={{
            width: "48px",
            height: "26px",
            borderRadius: "13px",
            background: isDefaultProfile ? "var(--btn-color)" : "rgba(150,150,150,0.3)",
            position: "relative",
            cursor: "pointer",
            flexShrink: 0,
            transition: "background 0.2s",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "3px",
              left: isDefaultProfile ? "25px" : "3px",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              background: "#fff",
              transition: "left 0.2s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-color)" }}>
            Set as Default Profile
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.5, marginTop: "2px" }}>
            This profile will be pre-selected at checkout
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          paddingTop: "4px",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={S.btnOutline}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (validate())
              onSave({ name, rules, deletedIds, id: initial?.id, isDefaultProfile });
          }}
          style={{
            ...S.btnPrimary,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="mdi mdi-loading mdi-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="mdi mdi-check" /> Save Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Root component ──────────────────────────────────────────────
export default function DiscountingProfile() {
  const merchantId = getMerchantId();
  const isReadOnly = currentPageAccess() === "READ";

  const [profiles, setProfiles] = useState([]);
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // ── Load profiles ──
  const loadProfiles = () => {
    setLoading(true);
    api
      .fetchAll(merchantId)
      .then((res) => {
        if (res.status) {
          setProfiles(groupIntoProfiles(res.data || []));
        } else {
          showToast("error", res.message || "Failed to load discounts.");
        }
      })
      .catch((err) => showHttpError(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadProfiles();
  }, [merchantId]);

  // ── Save (add or edit) via batch ──
  const handleSave = async ({ name, rules, deletedIds, isDefaultProfile }) => {
    if (isReadOnly) {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setSaving(true);
    try {
      // 1. DELETE removed rules individually (no batch delete endpoint)
      for (const id of deletedIds || []) {
        const res = await api.remove(merchantId, id);
        if (res.status !== "success")
          throw new Error(res.message || "Delete failed");
      }

      // 2. Single batch call — split rules into updates (existing) and newDiscounts (new)
      const updates = rules
        .filter((r) => r.id !== null)
        .map((r) => ({
          id: r.id,
          minimumCartValue: parseFloat(r.cartThreshold),
          discountPercentage: parseFloat(r.discountValue),
        }));

      const newDiscounts = rules
        .filter((r) => r.id === null)
        .map((r) => ({
          minimumCartValue: parseFloat(r.cartThreshold),
          discountPercentage: parseFloat(r.discountValue),
        }));

      const res = await api.batch({
        merchantId,
        profileName: name,
        updates,
        newDiscounts,
        isDefaultProfile: isDefaultProfile ? 1 : 0,
      });

      if (!res.status) throw new Error(res.message || "Save failed");

      // 3. Map batch response back to UI rule shape
      const savedRules = (res.data || []).map((rec) => ({
        id: rec.id,
        cartThreshold: String(rec.minimumCartValue),
        discountType: "percent",
        discountValue: String(rec.discountPercentage),
      }));

      const updatedProfile = { id: name, name, rules: savedRules };
      setProfiles((prev) => {
        const exists = prev.find((p) => p.name === name);
        return exists
          ? prev.map((p) => (p.name === name ? updatedProfile : p))
          : [...prev, updatedProfile];
      });

      showToast(
        "success",
        res.message ||
          (editing
            ? "Profile updated successfully."
            : "Profile created successfully.")
      );
      setMode("list");
      setEditing(null);
      loadProfiles();
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete profile (all rules) ──
  const handleDeleteProfile = (profile) => {
    if (isReadOnly) {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    showConfirm(
      "Delete Profile",
      `Are you sure you want to delete <strong>${profile.name}</strong> and all its rules? This action cannot be undone.`,
      "warning",
      async () => {
        setDeletingId(profile.id);
        try {
          for (const rule of profile.rules) {
            if (rule.id !== null) {
              const res = await api.remove(merchantId, rule.id);
              if (!res.status) throw new Error(res.message || "Delete failed");
            }
          }
          showToast("success", `Profile "${profile.name}" deleted.`);
          loadProfiles();
        } catch (err) {
          showHttpError(err);
        } finally {
          setDeletingId(null);
        }
      }
    );
  };

  const ruleSummary = (r) => `${r.discountValue}% off`;

  return (
    <Fragment>
      <ResponsiveStyles />
      <style>{`
        .dp-input::placeholder {
          color: var(--text-color);
          opacity: 0.35;
        }
      `}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="dp-wrapper" style={S.pageWrapper}>
            {/* Page header */}
            <div className="dp-header-row" style={S.headerRow}>
              <div>
                {(mode === "add" || mode === "edit") && (
                  <span
                    onClick={() => {
                      setMode("list");
                      setEditing(null);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--btn-color)",
                      cursor: "pointer",
                      marginBottom: 8,
                      userSelect: "none",
                      opacity: 0.85,
                      transition: "opacity .15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = 0.85;
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    <i
                      className="mdi mdi-arrow-left"
                      style={{ fontSize: 14 }}
                    />
                    Back to Discounts
                  </span>
                )}
                <h2 style={S.pageTitle}>
                  {mode === "add"
                    ? "Create Discounts Profile"
                    : mode === "edit"
                    ? `Edit — ${editing?.name}`
                    : "Discounts"}
                </h2>
                {mode === "list" && (
                  <div style={S.pageSubtitle}>
                    Create rule-based cart discounts — up to {MAX_RULES} rules
                    per profile
                  </div>
                )}
              </div>
              {mode === "list" && !isReadOnly && (
                <button
                  style={S.btnPrimary}
                  onClick={() => {
                    setEditing(null);
                    setMode("add");
                  }}
                >
                  <i className="mdi mdi-plus" /> Add Profile
                </button>
              )}
            </div>

            {/* Loading skeleton */}
            {loading && (
              <div style={{ ...S.card, padding: "40px", textAlign: "center" }}>
                <i
                  className="mdi mdi-loading mdi-spin"
                  style={{
                    fontSize: "28px",
                    color: "var(--btn-color)",
                    display: "block",
                    marginBottom: "12px",
                  }}
                />
                <div
                  style={{
                    fontSize: "13px",
                    color: "var(--text-color)",
                    opacity: 0.5,
                  }}
                >
                  Loading discount profiles…
                </div>
              </div>
            )}

            {/* Form (add / edit) */}
            {!loading && (mode === "add" || mode === "edit") && (
              <div style={S.card}>
                <div className="dp-card-header" style={S.cardHeader}>
                  <div
                    style={{
                      ...S.cardHeaderIcon,
                      background:
                        "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))",
                      color: "#22c55e",
                    }}
                  >
                    <i
                      className={
                        mode === "add"
                          ? "mdi mdi-plus"
                          : "mdi mdi-pencil"
                      }
                    />
                  </div>
                  <h4 style={S.cardHeaderTitle}>
                    {mode === "add"
                      ? "New Discounts Profile"
                      : `Edit — ${editing?.name}`}
                  </h4>
                </div>
                <div className="dp-card-body" style={S.cardBody}>
                  <ProfileForm
                    initial={editing}
                    onSave={handleSave}
                    onCancel={() => {
                      setMode("list");
                      setEditing(null);
                    }}
                    saving={saving}
                  />
                </div>
              </div>
            )}

            {/* Profile cards */}
            {!loading &&
              mode === "list" &&
              profiles.map((p) => {
                const sorted = [...p.rules].sort(
                  (a, b) =>
                    parseFloat(a.cartThreshold || 0) -
                    parseFloat(b.cartThreshold || 0)
                );
                const isDeleting = deletingId === p.id;
                return (
                  <div
                    key={p.id}
                    style={{
                      ...S.card,
                      opacity: isDeleting ? 0.6 : 1,
                      transition: "opacity 0.2s",
                    }}
                  >
                    <div className="dp-card-header" style={S.cardHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            ...S.cardHeaderIcon,
                            background:
                              "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))",
                            color: "#22c55e",
                          }}
                        >
                          <i className="mdi mdi-tag-multiple" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <h4 style={{ ...S.cardHeaderTitle, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</h4>
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "20px",
                              fontSize: "10px",
                              fontWeight: "600",
                              background: "rgba(34,197,94,0.1)",
                              color: "#22c55e",
                              display: "inline-block",
                              marginTop: "2px"
                            }}
                          >
                            {p.rules.length} rule{p.rules.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                      {!isReadOnly && (
                        <div
                          className="dp-card-header-actions"
                          style={{
                            marginLeft: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {(p.isDefaultProfile === 1 || p.isDefaultProfile === true) && (
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "20px",
                                fontSize: "11px",
                                fontWeight: "700",
                                background: "rgba(59,130,246,0.12)",
                                color: "#3b82f6",
                                border: "1px solid rgba(59,130,246,0.25)",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <i className="mdi mdi-star" style={{ fontSize: "12px" }} />
                              Default
                            </span>
                          )}
                          <button
                            onClick={() => {
                              setEditing(p);
                              setMode("edit");
                            }}
                            style={S.btnOutline}
                            disabled={isDeleting}
                          >
                            <i className="mdi mdi-pencil" /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProfile(p)}
                            style={{
                              ...S.btnDanger,
                              opacity: isDeleting ? 0.5 : 1,
                              cursor: isDeleting ? "not-allowed" : "pointer",
                            }}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <i
                                className="mdi mdi-loading mdi-spin"
                                style={{ fontSize: "14px" }}
                              />
                            ) : (
                              <i className="mdi mdi-delete" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="dp-card-body" style={S.cardBody}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {sorted.map((r, si) => {
                          const color = ruleColor(si);
                          return (
                            <div
                              key={r.id}
                              className="dp-rule-item"
                            >
                              <div
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  background: color,
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "13px",
                                  color: "var(--text-color)",
                                  opacity: 0.55,
                                  flexShrink: 0,
                                }}
                              >
                                If cart &gt;
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "var(--text-color)",
                                  fontFamily: "'SF Mono','Fira Code',monospace",
                                  flexShrink: 0,
                                }}
                              >
                                ${parseFloat(r.cartThreshold).toFixed(2)}
                              </span>
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color,
                                  flexShrink: 0,
                                }}
                              >
                                →
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color,
                                }}
                              >
                                {ruleSummary(r)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

            {/* Empty state */}
            {!loading && mode === "list" && profiles.length === 0 && (
              <div style={S.card}>
                <div style={{ textAlign: "center", padding: "52px 24px" }}>
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, rgba(34,197,94,0.12), rgba(34,197,94,0.04))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                      fontSize: "24px",
                      color: "#22c55e",
                    }}
                  >
                    <i className="mdi mdi-tag-multiple" />
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "var(--text-color)",
                      marginBottom: "6px",
                    }}
                  >
                    No discounts profiles
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "var(--text-color)",
                      opacity: 0.5,
                    }}
                  >
                    Create a profile and add rules like{" "}
                    <em>"If cart &gt; $100 → 10% off"</em>
                  </div>
                  {!isReadOnly && (
                    <button
                      style={{ ...S.btnPrimary, marginTop: "18px" }}
                      onClick={() => {
                        setEditing(null);
                        setMode("add");
                      }}
                    >
                      <i className="mdi mdi-plus" /> Add Discount Profile
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
